import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Disc3, Crown, Moon, Sun, Phone, MessageSquare } from 'lucide-react';
export function AboutSection() {
  const features = [{
    icon: Disc3,
    title: 'Live DJ Nights',
    description: 'Every Friday & Saturday featuring top DJs spinning the hottest tracks while you enjoy premium bottle service.',
    detail: 'Fridays & Saturdays • 11PM-3AM'
  }, {
    icon: Crown,
    title: 'VIP Experiences',
    description: 'Exclusive access to our premium spaces with dedicated staff, custom menus, and bottle service packages.',
    detail: null
  }, {
    icon: Moon,
    title: 'After Hours',
    description: 'The hidden gem for industry insiders and night owls. Curated playlists, craft mocktails, and immersive experiences.',
    detail: null
  }, {
    icon: Sun,
    title: 'Daytime',
    description: 'Gourmet dining, sports viewing, and premium hookah in a sophisticated atmosphere.',
    detail: 'Premium Cocktails • Gourmet Dining • Premium Hookah'
  }];
  return <section id="about" className="py-16 md:py-20 bg-[#0B1014] relative overflow-hidden" data-id="element-53">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5" data-id="element-54">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#DDA247] rounded-full blur-3xl" data-id="element-55" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#DDA247] rounded-full blur-3xl" data-id="element-56" />
      </div>

      <div className="container mx-auto px-6 relative z-10" data-id="element-57">
        <div className="max-w-4xl mx-auto text-center mb-12 md:mb-16" data-id="element-58">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-59">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6 tracking-wide" data-id="element-60">
              ABOUT US
            </h2>
            <div className="w-16 md:w-24 h-1 bg-[#DDA247] mx-auto mb-6 md:mb-8" data-id="element-61" />
            <p className="text-base md:text-xl text-gray-300 leading-relaxed px-2 md:px-0" data-id="element-62">
              House of Flavor is a destination-based experience platform in
              Laurel, Maryland. We bring bold flavors, premium hookah, and
              electrifying music together in one unforgettable destination.
              Whether you're here for our legendary Melodies & Mimosas brunch, a
              sophisticated happy hour, or elite nightlife, we ensure every
              visit is a curated experience.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 mt-12 md:mt-16" data-id="element-63">
          {features.map((feature, index) => <motion.div key={feature.title} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.6
        }} className="text-center group" data-id="element-64">
              <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 mb-3 md:mb-4 bg-[#DDA247]/10 rounded-full group-hover:bg-[#DDA247]/20 transition-colors duration-300" data-id="element-65">
                <feature.icon className="w-6 h-6 md:w-8 md:h-8 text-[#DDA247]" data-id="element-66" />
              </div>
              <h3 className="text-base md:text-xl font-bold text-white mb-1 md:mb-2 tracking-wide" data-id="element-67">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-[10px] md:text-sm mb-2 md:mb-3 px-2 md:px-0 line-clamp-2 md:line-clamp-none" data-id="element-68">
                {feature.description}
              </p>
              {feature.detail && <p className="text-[#DDA247] text-[8px] md:text-xs font-medium tracking-widest uppercase" data-id="element-69">
                  {feature.detail}
                </p>}
            </motion.div>)}
        </div>

        {/* Booking CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mx-auto mt-12 md:mt-16" data-id="element-70">
          <div className="relative rounded-2xl md:rounded-3xl border border-[#DDA247]/30 bg-[#11161A] p-8 md:p-12 text-center overflow-hidden" data-id="element-71">
            <div className="absolute inset-0 z-0 opacity-20" data-id="element-72">
              <img src="https://cdn.magicpatterns.com/uploads/2rjXUfMZfcG9xoKVhqBw5u/HOF_8.jpg" alt="HOF Venue" className="w-full h-full object-cover" data-id="element-73" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#11161A] via-[#11161A]/80 to-[#11161A]" data-id="element-74" />
            </div>
            <div className="relative z-10" data-id="element-75">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#DDA247]/5 rounded-full blur-3xl pointer-events-none" data-id="element-76" />
              <p className="text-[#DDA247] text-xs md:text-sm font-bold tracking-[0.2em] uppercase mb-3" data-id="element-77">
                Reserve Your Experience
              </p>
              <h3 className="text-2xl md:text-4xl font-bold text-white mb-3 tracking-wide" data-id="element-78">
                Book Your Table Today
              </h3>
              <p className="text-gray-400 text-sm md:text-base mb-8 max-w-xl mx-auto leading-relaxed" data-id="element-79">
                Skip the wait and lock in your spot. Text us to book or call our
                team directly — we're ready to make your night unforgettable.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-80">
                <a href="sms:3014549401" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#DDA247] text-[#0B1014] font-bold text-sm uppercase tracking-widest rounded-full hover:bg-[#c98f35] transition-colors duration-300" data-id="element-81">
                  <MessageSquare className="w-4 h-4" data-id="element-82" />
                  Book by Text
                </a>
                <a href="tel:3014549401" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border border-white/20 text-white font-bold text-sm uppercase tracking-widest rounded-full hover:border-[#DDA247] hover:text-[#DDA247] transition-colors duration-300" data-id="element-83">
                  <Phone className="w-4 h-4" data-id="element-84" />
                  Call (301) 454-9401
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}